import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearSession, clearSessionStorage, setSession } from '@/lib/session'
import { isAmplifyConfigured } from '@/lib/amplify'
import { buildAuthSession } from '../session'
import { isInAppReturnPath, rememberReturnTo, resolvePostLoginTarget } from '../returnTo'
import { getCurrentCognitoSession } from '../amplifyAuth'
import AuthLogo from '../components/AuthLogo'
import AuthHighlights from '../components/AuthHighlights'
import AuthFooterLinks from '../components/AuthFooterLinks'
import LoginForm from './components/LoginForm'
import {
  clearExtensionIntent,
  handleExtensionCheckMessage,
  postAuthSuccessToExtension,
  readExtensionIntentFromQuery,
  restoreExtensionIntent,
  saveExtensionIntent,
  type ExtensionLoginIntent,
} from '../extensionAuth'
import { authLog } from '@/features/auth/debug'
import './Login.css'

const OAUTH_RETRY_DELAYS_MS = [0, 500, 1000, 2000]

function isOAuthCallback(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.has('code') || params.has('state')
}

async function getAuthenticatedUserAfterOAuth(): Promise<Awaited<ReturnType<typeof getCurrentCognitoSession>>> {
  authLog('OAuth callback detected (code/state in URL), waiting for token exchange')
  for (let i = 0; i < OAUTH_RETRY_DELAYS_MS.length; i++) {
    const delay = OAUTH_RETRY_DELAYS_MS[i] ?? 0
    if (delay > 0) await new Promise((r) => setTimeout(r, delay))
    try {
      const user = await getCurrentCognitoSession()
      if (user.idToken) {
        authLog('OAuth callback: got user after attempt', i + 1)
        return user
      }
    } catch (e) {
      authLog('OAuth callback: currentAuthenticatedUser attempt', i + 1, 'failed', e)
      if (i === OAUTH_RETRY_DELAYS_MS.length - 1) throw new Error('not authenticated')
    }
  }
  throw new Error('not authenticated')
}

function postExtensionLoginIfApplicable(
  intent: ExtensionLoginIntent,
  nextSession: { idToken: string; refreshToken: string; tokenExpiry: string }
): boolean {
  if (!intent.isExtension) return false
  if (nextSession.idToken && nextSession.refreshToken && nextSession.tokenExpiry) {
    postAuthSuccessToExtension({
      idToken: nextSession.idToken,
      refreshToken: nextSession.refreshToken,
      tokenExpiry: nextSession.tokenExpiry,
    })
  }
  clearExtensionIntent()
  if (intent.redirectUri) {
    window.location.href = intent.redirectUri
    return true
  }
  return false
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isUpdateEmail = searchParams.get('action') === 'update-email'
  const updateEmailId = searchParams.get('id')
  const fromOAuth = isOAuthCallback()
  const [isCheckingAuth, setIsCheckingAuth] = useState(fromOAuth && isAmplifyConfigured())
  const [extensionIntent, setExtensionIntent] = useState<ExtensionLoginIntent>(() => {
    const fromQuery = readExtensionIntentFromQuery()
    if (fromQuery.isExtension) return fromQuery
    return restoreExtensionIntent()
  })
  const stateFrom: unknown = (location.state as { from?: unknown } | null)?.from
  const returnTo = isInAppReturnPath(stateFrom) ? stateFrom : undefined

  // Declared before the session check below, which can redirect as soon as it resolves.
  useEffect(() => {
    rememberReturnTo(returnTo)
  }, [returnTo])

  useEffect(() => {
    const queryIntent = readExtensionIntentFromQuery()
    const persistedIntent = restoreExtensionIntent()
    const effectiveIntent = queryIntent.isExtension ? queryIntent : persistedIntent
    if (effectiveIntent.isExtension) {
      setExtensionIntent(effectiveIntent)
      saveExtensionIntent(effectiveIntent)
    } else {
      setExtensionIntent({ isExtension: false, redirectUri: null })
      clearExtensionIntent()
    }

    const messageHandler = (event: MessageEvent) => handleExtensionCheckMessage(event)
    window.addEventListener('message', messageHandler)

    let cancelled = false

    if (isUpdateEmail && updateEmailId) {
      authLog('Login page: update-email flow detected, clearing any existing session and skipping auto-resume')
      clearSession()
      setIsCheckingAuth(false)
      return () => {
        cancelled = true
        window.removeEventListener('message', messageHandler)
      }
    }

    if (!isAmplifyConfigured()) {
      return () => {
        cancelled = true
        window.removeEventListener('message', messageHandler)
      }
    }

    const isReturnFromOAuth = isOAuthCallback()
    if (isReturnFromOAuth) authLog('Login page: detected OAuth return, checking session with retries')
    const check = isReturnFromOAuth ? getAuthenticatedUserAfterOAuth() : getCurrentCognitoSession()
    check
      .then((user) => {
        if (cancelled || !user.idToken) {
          authLog('Login page: no valid session', { cancelled, hasUser: !!user })
          setIsCheckingAuth(false)
          return
        }
        const idToken = user.idToken
        const refreshToken = user.refreshToken
        const email = user.email
        if (!idToken || !email) {
          authLog('Login page: missing idToken or email')
          setIsCheckingAuth(false)
          return
        }
        authLog('Login page: federated session valid, building app session and redirecting')
        clearSessionStorage()
        return buildAuthSession(email, idToken, refreshToken, user.expirySeconds).then((nextSession) => {
          if (cancelled) return
          setSession(nextSession)
          const latestIntent = restoreExtensionIntent()
          const redirectedToExtension = postExtensionLoginIfApplicable(latestIntent, nextSession)
          if (redirectedToExtension) return
          return resolvePostLoginTarget(nextSession, returnTo).then((redirectPath) => {
            if (cancelled) return
            authLog('Login page: redirecting to latest context route', redirectPath)
            navigate(redirectPath, { replace: true })
          })
        })
      })
      .catch((e: unknown) => {
        authLog('Login page: session check failed', e)
        setIsCheckingAuth(false)
      })
    return () => {
      cancelled = true
      window.removeEventListener('message', messageHandler)
    }
  }, [navigate, isUpdateEmail, updateEmailId, returnTo])

  if (fromOAuth && isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#d1e8e8]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0d9488] border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="login-page min-h-screen bg-[#d1e8e8]">
      <div className="px-[1.5rem] pt-[1.5rem] sm:px-[2rem]">
        <AuthLogo className="h-[3rem] w-auto" />
      </div>

      <main className="px-[1.5rem] pb-[2.5rem] pt-[2.25rem] sm:px-[2rem] lg:px-[2.5rem]">
        <div className="mx-auto grid w-full max-w-[78rem] grid-cols-1 items-start gap-[2.5rem] lg:grid-cols-[1fr_28rem] lg:gap-[4rem]">
          <section className="max-w-[33rem] lg:pt-[1.5rem]">
            <h1 className="font-display text-[2.25rem] font-bold leading-[1.08] text-gray-900">Bring your business to life</h1>
            <p className="mt-[0.25rem] text-[1.625rem] leading-[1.1] text-gray-800">a new way of working</p>
            <AuthHighlights compact />
          </section>

          <section className="w-full">
            <div className="rounded-[1rem] border border-[#bde2e1] bg-[#efefef] p-[1.5rem] shadow-none">
              <h2 className="font-display text-[1.75rem] font-medium leading-[1.2] text-gray-900">Sign in to Dashboard Insights</h2>
              <p className="mt-[0.25rem] text-[0.875rem] text-gray-700">Please enter your details</p>
              {extensionIntent.isExtension && (
                <div className="extension-login-notice">
                  <svg
                    className="extension-login-notice__icon"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm1 8a1 1 0 100-2v-4a1 1 0 112 0v4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <div className="extension-login-notice__title">Extension Login</div>
                    <div className="extension-login-notice__text">
                      You&apos;re signing in to authenticate your Dashboard Insights Extension.
                    </div>
                  </div>
                </div>
              )}
              {isUpdateEmail && updateEmailId && (
                <div className="extension-login-notice">
                  <svg
                    className="extension-login-notice__icon"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 8a1.25 1.25 0 100-2.5A1.25 1.25 0 0010 15z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <div className="extension-login-notice__title">Email Update</div>
                    <div className="extension-login-notice__text">
                      Sign in to confirm your email change before continuing. Please use your old email address to sign in.
                    </div>
                  </div>
                </div>
              )}
              <LoginForm
                onSuccess={(path: string) => navigate(path, { replace: true })}
                returnTo={returnTo}
                extensionIntent={extensionIntent}
                updateEmailFlow={{ enabled: isUpdateEmail && Boolean(updateEmailId), id: updateEmailId }}
              />
              <AuthFooterLinks compact />
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
